/* eslint-disable no-mixed-operators */
import * as React from 'react';
import { IColumn, ConstrainMode, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { connect } from 'react-redux';
import { DirectionalHint, IListProps, Link, ShimmeredDetailsList, TooltipHost } from '@fluentui/react';
import _ from 'lodash';
import { IUserProfile } from '../../Auth/types';
import { showDialog, showToastMessage } from '../../Dialog/actions';
import { ICancelRequest, IOrder } from '../types';
import Helper from '../../../core/libraries/Helper';
import Template from '../../../core/libraries/Template';
import RbLabel, { LabelSize } from '../../../bosch-react/components/label/RbLabel';
import RbButton, { ButtonSize, ButtonType } from '../../../bosch-react/components/button/RbButton';
import { cancelWorkflow, getApprovedOrders, getExpiredOrders } from '../services';
import Constants from '../../../core/libraries/Constants';
import ListActionsMenu from '../../../core/common/ListActionsMenu';
import DetailsListWrapper from '../../../core/components/DetailsListWrapper/DetailsListWrapper';
import Configuration from '../../../core/libraries/Configuration';
import { RootState } from '../../../store/store';
import { convertPeopleAndGroupObjectArrToStringArr } from '../../../utils/converter';

export interface MyApprovedOrdersProps {
    userProfile: IUserProfile | undefined,
    showDialog: typeof showDialog,
    showToastMessage: typeof showToastMessage,
}

export interface IState {
    isAdminMode: boolean,
    approvedOrders: IOrder[] | null,
    filteredItems: IOrder[] | null,
    selectedItem: IOrder | undefined,
    showCancelDialog: boolean,
    showWorkflowIdDialog: boolean,
}

class MyApprovedOrders extends React.Component<MyApprovedOrdersProps, IState> {
    private timeoutId: NodeJS.Timeout | null = null;


    constructor(props: MyApprovedOrdersProps) {
        super(props);
        this.state = {
            isAdminMode: false,
            selectedItem: undefined,
            approvedOrders: null,
            showCancelDialog: false,
            showWorkflowIdDialog: false,
            filteredItems: null
        };
    }

    async componentDidMount() {
        Helper.runNewTask(() => {
            this.getApprovedOrders();
        });
    }

    shimmeredDetailsListProps: IListProps = {
        renderedWindowsAhead: 0,
        renderedWindowsBehind: 0,
    };

    searchText(value: any, columns: IColumn[]) {

    }

    changeMode() {
        this.setState({
            isAdminMode: !this.state.isAdminMode
        }, async () => {
            await this.getApprovedOrders();
        })
    }

    normalModeColumns: IColumn[] = [
        {
            key: 'title',
            name: 'Title',
            fieldName: 'Title',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <Link target='_blank' href={Helper.generateAccessMediatorUrl(item.reportId)}>{item.title}</Link>
                );
            }
        },
        {
            key: 'authors',
            name: 'Author(s)',
            fieldName: 'Authors',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return Template.renderReadOnlyPeople(convertPeopleAndGroupObjectArrToStringArr(item.authors), item.authorDisplayName);
            }
        },
        {
            key: 'orderApprover',
            name: 'Order Approver',
            fieldName: 'Approver',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                let approvers = !!item.approvers && item.approvers.split(';');
                return Template.renderReadOnlyPeople(!!approvers && approvers.length > 0 && approvers || []);
            }
        },
        {
            key: 'orderComment',
            name: 'Order Comments',
            fieldName: 'Comment',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <TooltipHost
                        directionalHint={DirectionalHint.leftCenter}
                        content={item.comment}>
                        <RbLabel size={LabelSize.Small} >{item.comment}</RbLabel>
                    </TooltipHost>
                );
            }
        },
        {
            key: 'orderDate',
            name: 'Order Date',
            fieldName: 'OrderDate',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (<RbLabel size={LabelSize.Small} >{Helper.getDateTimeFormatForUI(item.created)}</RbLabel>);
            }
        },
        {
            key: 'expiryDate',
            name: 'Expiry Date',
            fieldName: 'ExpiryDate',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (<RbLabel size={LabelSize.Small} >{Helper.getDateTimeFormatForUI(item.expiryDate)}</RbLabel>);
            }
        }
    ];

    adminModeColumns: IColumn[] = [
        {
            key: 'orderUser',
            name: 'Order User',
            fieldName: 'OrderUser',
            currentWidth: Helper.resizeColumnByScreenWidth(12),
            minWidth: Helper.resizeColumnByScreenWidth(12),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return Template.renderReadOnlyPeople([item.createdBy]);
            }
        }
        ,
        {
            key: 'correlationId',
            name: 'Correlation ID',
            fieldName: 'CorrelationId',
            currentWidth: Helper.resizeColumnByScreenWidth(8),
            minWidth: Helper.resizeColumnByScreenWidth(8),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <TooltipHost
                        directionalHint={DirectionalHint.leftCenter}
                        content={item.correlationId}>
                        <RbLabel size={LabelSize.Small} >{item.correlationId}</RbLabel>
                    </TooltipHost>
                );
            }
        }
    ]

    actionColumns: IColumn[] = [
        {
            key: 'notice',
            name: 'Notice',
            fieldName: 'Notice',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (
                    (Helper.isDateWithinNext7Days(item.expiryDate) === true) ?
                        <RbLabel size={LabelSize.Small} >This order is expiring soon</RbLabel> : <></>
                );
            }
        },
        {
            key: 'renew',
            name: '',
            fieldName: 'Actions',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (
                    item.orderStatus === Constants.STATUS.EXPIRED ? <RbButton label="Renew Order" size={ButtonSize.Small} onClick={() => {
                        Helper.openAMPage(item.reportId)
                    }} /> : <></>
                );
            }
        }
    ]

    async getApprovedOrders() {
        this.setState({
            filteredItems: null
        })
        let approvedOrders: IOrder[] = [];
        let expiredOrders: IOrder[] = [];
        try {
            approvedOrders = await getApprovedOrders(this.state.isAdminMode);
            expiredOrders = await getExpiredOrders(this.state.isAdminMode);
            this.setState({
                approvedOrders: [...approvedOrders, ...expiredOrders],
                filteredItems: [...approvedOrders, ...expiredOrders]
            },
                () => {
                    this.props.showDialog(false)
                })
        } catch (error) {
            this.props.showDialog(false)
        }
    }

    private _onChangeText = (text: string): void => {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.timeoutId = setTimeout(() => {
            this.setState({
                filteredItems: text ? [...this.state.approvedOrders ?? []].filter(i => 
                    (i.title && i.title.toLowerCase().indexOf(text.toLowerCase()) > -1) ||
                    (i.workflowId && i.workflowId.toLowerCase().indexOf(text.toLowerCase()) > -1)
                ) : [...this.state.approvedOrders ?? []],
            });
        }, 500);
    };

    async cancelOrder(reason: string) {
        if (reason !== "") { // OK
            if (!!this.props.userProfile) {
                this.props.showDialog(Constants.DIALOG_MESSAGE.CANCEL_ORDERED_REPORT);
                try {
                    var request = {
                        orderId: this.state.selectedItem?.id ?? -1,
                        comment: reason
                    } as ICancelRequest
                    let response = await cancelWorkflow(request)
                    if (response.error === false) {
                        this.props.showToastMessage(
                            Constants.TOAST_MESSAGE_CODE.SUCCESS,
                            Constants.CANCEL_REPORT.ORDER.SUCCESS
                        );
                        await this.getApprovedOrders();
                    }
                    else {
                        this.props.showToastMessage(
                            Constants.TOAST_MESSAGE_CODE.ERROR,
                            Constants.CANCEL_REPORT.ORDER.FAILED
                        );
                    }
                    this.props.showDialog(false);
                    this.setState({
                        selectedItem: undefined,
                        showCancelDialog: false
                    });
                } catch (error) {
                    this.props.showToastMessage(
                        Constants.TOAST_MESSAGE_CODE.ERROR,
                        Constants.CANCEL_REPORT.ORDER.FAILED
                    );
                    this.props.showDialog(false);
                    this.setState({
                        selectedItem: undefined,
                        showCancelDialog: false
                    });
                }
            }
            else {
                this.props.showToastMessage(
                    Constants.TOAST_MESSAGE_CODE.ERROR,
                    "Something went wrong with your permission. Please try again! If the problem persists, Please click the Support button located below your avatar for assistance"
                );
                this.props.showDialog(false);
                this.setState({
                    selectedItem: undefined,
                    showCancelDialog: false
                });
            }
        }
        else { // Cancel
            this.setState({
                selectedItem: undefined,
                showCancelDialog: false
            });
        }
    }

    render() {
        let { filteredItems } = this.state
        let modeColumns = this.state.isAdminMode && [...this.normalModeColumns, ...this.adminModeColumns, ...this.actionColumns] || [...this.normalModeColumns, ...this.actionColumns];
        let element = (
            <div className="ms-Grid">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm1" />
                    <div className="ms-Grid-col ms-sm10">
                        <div className="ms-Grid">
                            <div className="ms-Grid-row">
                                <RbLabel className="header-title" size={LabelSize.Large}>Approved Order Reports</RbLabel>
                                <ListActionsMenu
                                    columns={modeColumns}
                                    items={filteredItems || []}
                                    searchText={(value: any) => { this._onChangeText(value) }}
                                    showAdminResults={this.state.isAdminMode}
                                    getAdminData={() => {
                                        this.changeMode();
                                    }} />
                            </div>
                            <DetailsListWrapper>
                                <ShimmeredDetailsList
                                    setKey="set"
                                    compact={true}
                                    items={filteredItems || []}
                                    constrainMode={ConstrainMode.unconstrained}
                                    columns={modeColumns}
                                    selectionMode={SelectionMode.single}
                                    enableShimmer={!filteredItems}
                                    ariaLabelForShimmer="Content is being fetched"
                                    ariaLabelForGrid="Approved order details"
                                    listProps={this.shimmeredDetailsListProps}
                                    onRenderDetailsHeader={Configuration.onRenderStickyDetailsHeader}
                                    onShouldVirtualize={() => { return true }}
                                />
                            </DetailsListWrapper>
                        </div>
                    </div>
                    <div className="ms-Grid-col ms-sm1" />
                </div>
            </div>
        );
        return element;
    }
}

const mapStateToProps = (state: RootState) => ({
    userProfile: state.auth.userProfile,
});

export default connect(mapStateToProps, {
    showDialog,
    showToastMessage,
})(MyApprovedOrders);